import type {
  AuthCtx,
  GencowAuthConfig,
  GencowCtx,
  GencowDefaultUserIdentity,
  GencowProcedureDef,
  RealtimeCtx,
  RealtimeFanout,
  RlsSessionContext,
} from "@gencow/core";
import { buildRealtimeCtx, createRlsDb, withRetry } from "@gencow/core";
import {
  buildRlsSessionContextFromAuthConfig,
  emptyRlsSessionContextFromAuthConfig,
} from "./build-rls-session-context.js";
import { emitRagOperationMetric } from "./rag-operations-metrics.js";
import { createGroundingRuntime } from "./server-grounded-answer-runtime.js";
import { parseSearchTierConfig } from "./server-search-config.js";
import { createSearchRuntime } from "./server-search-runtime.js";
import { createServerServices } from "./server-document-runtime.js";
import { buildPlatformRequestRlsContext } from "./server-platform-rls.js";

type SchedulerLike = {
  runAfter(delayMs: number, actionName: string, args?: unknown): unknown;
  runAt(at: Date, actionName: string, args?: unknown): unknown;
  cancel(jobId: string): boolean;
  cron(name: string, cronExpr: string, actionName: string): void;
  registerAction(name: string, handler: (args: unknown) => Promise<void>): void;
  executeAction(name: string, args?: unknown): Promise<void>;
};

type HonoLikeContext = {
  get(key: string): unknown;
};

type RequestContextDeps = {
  db: unknown;
  systemDb?: unknown;
  rawSql?: unknown;
  analyticsRawSql?: unknown;
  storage: unknown;
  scheduler?: SchedulerLike | null;
  isBaas: boolean;
  isPlatform?: boolean;
  appName: string;
  internalToken: string;
  authConfig?: GencowAuthConfig;
  realtimeFanout: RealtimeFanout;
  deferRealtimeFanout?: boolean;
  procedureQueryMap?: Map<string, GencowProcedureDef<"query", any, any, any>>;
  buildRealtimeCtxImpl?: typeof buildRealtimeCtx;
  createRlsDbImpl?: typeof createRlsDb;
  buildRlsSessionContextImpl?: typeof buildRlsSessionContextFromAuthConfig;
  withRetryImpl?: typeof withRetry;
  createServicesImpl?: typeof createServerServices;
};

function resolveRequestRlsContext(params: {
  user: GencowDefaultUserIdentity | null;
  isPlatform?: boolean;
  authConfig?: GencowAuthConfig;
  buildRlsSessionContextImpl?: typeof buildRlsSessionContextFromAuthConfig;
}): RlsSessionContext {
  if (params.isPlatform) {
    return buildPlatformRequestRlsContext({
      userId: params.user?.id ?? "",
      role: (params.user as { role?: unknown } | null)?.role,
      isPlatform: true,
    });
  }

  const build =
    params.buildRlsSessionContextImpl ?? ((user, auth) => buildRlsSessionContextFromAuthConfig(user, auth));
  return build(params.user, params.authConfig);
}

export function createUnavailableScheduler(isBaas: boolean): SchedulerLike {
  return {
    runAfter: () => {
      throw new Error(
        isBaas
          ? "Scheduler not initialized — add defineApi({ crons }) or legacy gencow/crons.ts to enable. Note: In BaaS mode, scheduler.runAfter() is sleep-unsafe (timers are lost when app goes idle). Recommended: split into separate mutations and call sequentially from frontend."
          : "Scheduler not initialized — add defineApi({ crons }) or legacy gencow/crons.ts to enable.",
      );
    },
    runAt: () => {
      throw new Error(
        isBaas
          ? "Scheduler not initialized — add defineApi({ crons }) or legacy gencow/crons.ts to enable. Note: In BaaS mode, scheduler.runAt() is sleep-unsafe."
          : "Scheduler not initialized — add defineApi({ crons }) or legacy gencow/crons.ts to enable.",
      );
    },
    cancel: () => false,
    cron: () => {},
    registerAction: () => {},
    executeAction: async () => {},
  };
}

export function createRequestContextBuilder(
  params: RequestContextDeps,
): (honoCtx: HonoLikeContext) => GencowCtx {
  const buildRealtimeCtxImpl = params.buildRealtimeCtxImpl ?? buildRealtimeCtx;
  const createRlsDbImpl = params.createRlsDbImpl ?? createRlsDb;
  const buildRlsSessionContextImpl =
    params.buildRlsSessionContextImpl ?? buildRlsSessionContextFromAuthConfig;
  const withRetryImpl = params.withRetryImpl ?? withRetry;
  const systemDb = params.systemDb ?? params.db;
  // Search capability is captured once per app process. Plan/extension changes require restart or redeploy.
  const searchTierConfig = parseSearchTierConfig(process.env.__GENCOW_SEARCH_TIER_CONFIG);
  const createServicesImpl = params.createServicesImpl ?? createServerServices;

  const unavailableScheduler = params.scheduler ?? createUnavailableScheduler(params.isBaas);
  const services = createServicesImpl({
    appName: params.appName,
    db: params.db as never,
    storage: params.storage as never,
    appToken: params.internalToken,
    documentProxyUrl: process.env.GENCOW_DOCUMENT_PROXY_URL ?? null,
    allowPrivateExternalCorpora: (process.env.GENCOW_DOCUMENT_EXTERNAL_PRIVATE_CORPORA ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean),
    fetchImpl: fetch,
  });

  const noopRealtime: RealtimeCtx = {
    emit: () => {},
    invalidate: () => {},
    refresh: () => {},
    channel: () => ({
      authorize: () => {
        throw new Error("Authorized realtime channel tokens are not available in refresh context");
      },
      publish: () => {},
      invalidate: () => {},
      revoke: () => {},
    }),
    query: () => ({
      authorize: () => {
        throw new Error("Authorized realtime query tokens are not available in refresh context");
      },
      invalidate: () => {},
      revoke: () => {},
    }),
    invalidateScoped: () => {},
  };

  const observeRagMetric = (event: Parameters<typeof emitRagOperationMetric>[1]) =>
    emitRagOperationMetric(params.db as never, {
      appId: params.appName,
      ...event,
    });

  const buildRefreshCtx = (): GencowCtx => {
    const refreshAuth: AuthCtx = {
      getUserIdentity: () => null,
      requireAuth: () => {
        throw new Error("Auth not available in refresh context");
      },
    };

    const searchRuntime = createSearchRuntime({
      db: params.db as never,
      auth: refreshAuth,
      config: searchTierConfig,
      observeRagOperation: observeRagMetric,
    });
    const refreshRls = params.isPlatform
      ? buildPlatformRequestRlsContext({ userId: "", isPlatform: true })
      : emptyRlsSessionContextFromAuthConfig(params.authConfig);

    const partialCtx: any = {
      db: createRlsDbImpl(params.db as never, refreshRls),
      unsafeDb: systemDb,
      rawSql: params.rawSql,
      analyticsRawSql: params.analyticsRawSql ?? params.rawSql,
      auth: refreshAuth,
      storage: params.storage,
      realtime: noopRealtime,
      scheduler: unavailableScheduler,
      retry: withRetryImpl,
      services,
      ...searchRuntime,
      grounding: createGroundingRuntime({
        db: params.db as never,
        auth: refreshAuth,
        search: searchRuntime.search,
        observeRagAnswer: observeRagMetric,
      }),
    };

    return partialCtx as GencowCtx;
  };

  return (honoCtx: HonoLikeContext): GencowCtx => {
    const authCtx: AuthCtx = (honoCtx.get("auth") as AuthCtx | undefined) ?? {
      getUserIdentity: () => null,
      requireAuth: () => {
        throw new Error("Authentication required");
      },
    };

    const searchRuntime = createSearchRuntime({
      db: params.db as never,
      auth: authCtx,
      config: searchTierConfig,
      observeRagOperation: observeRagMetric,
    });
    const partialCtx: any = {
      auth: authCtx,
      storage: params.storage,
      realtime: buildRealtimeCtxImpl({
        fanout: params.realtimeFanout,
        buildCtxForRefresh: buildRefreshCtx,
        appName: params.appName,
        channelTokenSecret: params.internalToken,
        auth: authCtx,
        deferFanout: params.deferRealtimeFanout,
        getProcedureQuery: (key) => {
          const proc = params.procedureQueryMap?.get(key);
          if (!proc) return undefined;
          return { handler: proc.handler, inputSchema: proc.inputSchema };
        },
      }),
      scheduler: unavailableScheduler,
      retry: withRetryImpl,
      services,
      ...searchRuntime,
      grounding: createGroundingRuntime({
        db: params.db as never,
        auth: authCtx,
        search: searchRuntime.search,
        observeRagAnswer: observeRagMetric,
      }),
    };

    partialCtx.db = createRlsDbImpl(
      params.db as never,
      resolveRequestRlsContext({
        user: authCtx.getUserIdentity(),
        isPlatform: params.isPlatform,
        authConfig: params.authConfig,
        buildRlsSessionContextImpl,
      }),
    );
    partialCtx.unsafeDb = systemDb;
    partialCtx.rawSql = params.rawSql;
    partialCtx.analyticsRawSql = params.analyticsRawSql ?? params.rawSql;

    return partialCtx as GencowCtx;
  };
}
